// Form 11 - Assessment Summary (Read-only)
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Home as HomeIcon,
  LocationCity as LocationCityIcon,
  DirectionsCar as DirectionsCarIcon,
  Visibility as VisibilityIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";
import DoApiService from "../../../services/DoApiService";
import LaboratoryService from "../../../services/laboratoryService";
import { additionalDocumentService } from "../../../services/additionalDocumentService";
import Form11Service from "../../../services/Form11Service";
import PTProviderService from "../../../services/ptProviderService";
import {
  decryptAppId,
  decryptText,
  encryptAppId,
  encryptText,
} from "../../../utils/encryption";
import s3Utils from "../../../utils/s3Utils";
import { getApplicationStage } from "../../../services/api";

const Form11 = () => {
  // State management
  const [cabDetails, setCabDetails] = useState(null);
  const [TotalNc, setTotalNc] = useState(0);
  const [ncData, setNcData] = useState([]); // Store complete NC array
  const [assessorData, setAssessorData] = useState([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [nonConformities, setNonConformities] = useState({ total: 0 });
  const [assessmentReportData, setAssessmentReportData] = useState(null);
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loading, setLoading] = useState(true);
  // ADD THESE STATES WITH OTHER useState
  const [showAfterApprovalBox, setShowAfterApprovalBox] = useState(false);
  const [disabledApprovalBox, setDisabledApprovalBox] = useState(false);
  const [afterApprovalRemark, setAfterApprovalRemark] = useState("");
  const [error, setError] = useState(null);
  const [isDisplayNew, setIsDisplayNew] = useState(false);
  const [naf6Data, setNaf6Data] = useState(null);

  // Facility state management
  const [facilityTypes, setFacilityTypes] = useState({
    PermanentFacility: false,
    SiteFacility: false,
    MobileFacility: false,
  });

  // Decision state management
  const [decisionProcess, setDecisionProcess] = useState("");

  // Form inputs state management
  const [formInputs, setFormInputs] = useState({
    internalAudit: "",
    internalAuditType: "", // New field for dropdown selection
    trainedAuditor: "", // Yes/No for cl.8.8.3
    independenceOfActivity: "", // Yes/No for cl.8.8.3
    ncsClosed: "", // Yes/No/NA for cl.8.8.3
    effective: "", // Yes/No for cl.8.8.3
    frequencyOfAudit: "", // Text box for cl.8.8.3
    managementReview: "",
    managementReviewType: "", // New field for management review dropdown
    allPointsCovered: "", // Yes/No for cl.8.9.1
    currentStatus: "Applicant Lab",
    extnValidity: "",
    conductedWithinYear: "", // Yes/No for cl.8.9.1
    ptParticipation: "",
    ptAvailability: "", // Yes/No for PT availability
    ptNotAvailableReason: "", // Text box when PT is not available
    ptParticipationType: "", // Dropdown for assessment team if PT participated
    ptCoverageOfScope: "", // Adequate/Not Adequate
    ptOutlier: "", // Yes/No
    ptOutlierRemarks: "", // Text box for root cause analysis if outlier is Yes
    ilcParticipation: "",
    ilcCoverageOfScope: "", // Adequate/Not Adequate for coverage of scope through PT and/or ILC
    ilcOutlier: "", // Yes/No
    ilcOutlierRemarks: "", // Text box for root cause analysis if outlier is Yes
    otherQualityMeasures: "",
    advisorConsultant: "",
    adequacyOrganization: `Testing & Re-Testing- Adequate
Note - NA/Writing option in note, if something to be mentioned by DO.
Personnel to review the results and authorize the release of reports- Satisfactory.`,
    otherInformation: `No Misuse of the NABL symbol and No complaints against the laboratory.
No unannounced assessment was conducted for the laboratory.
`,
    observationRecommendation: `Recommended grant of accreditation in accordance with ISO 15189:2022 in the field of medical testing for discipline of (Fetch disciplines from application and also give writing option if there is some deletion of discipline/ non recommendation) as per the scope recommendation by the assessment team.
Test at Sl. No (writing option) marked inadequate in form 72 to be included in the scope of accreditation as per Corrective action for the NC Raised (Refer NC No) and comments from the assessor vide email dated: .
Dr. ABC is not accepted to review the results and authorise the release of reports as per the assessment report and request/ informed to the laboratory vide email dated: and acknowledged by laboratory email dated:`,
    acProcessComments: "",
    auditComments: "",
    managementComments: "",
  });

  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // JD forwarding state
  const [isFormSaved, setIsFormSaved] = useState(false);
  const [isForwarding, setIsForwarding] = useState(false);
  const [showForwardConfirm, setShowForwardConfirm] = useState(false);
  const [forwardSuccess, setForwardSuccess] = useState(false);
  const [forwardError, setForwardError] = useState(null);

  // Form11 data state
  const [existingForm11Data, setExistingForm11Data] = useState(null);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [loadingForm11Data, setLoadingForm11Data] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditModeAfterUpdate, setIsEditModeAfterUpdate] = useState(false);

  // Helper function to filter NCs by clause number
  const getNCsByClause = (clauseNumber) => {
    return ncData.filter((nc) => nc.clauseno === clauseNumber);
  };

  // Helper function to parse multiple images separated by ~
  const parseImages = (documentString) => {
    if (!documentString) return [];
    return documentString.split("~").filter((img) => img.trim() !== "");
  };
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // PT Data popup state
  const [ptData, setPtData] = useState([]);
  const [ptDataLoading, setPtDataLoading] = useState(false);
  const [ptDataError, setPtDataError] = useState(null);
  const [ptDataPopupOpen, setPtDataPopupOpen] = useState(false);

  // ILC Data popup state
  const [ilcData, setIlcData] = useState([]);
  const [ilcDataLoading, setIlcDataLoading] = useState(false);
  const [ilcDataError, setIlcDataError] = useState(null);
  const [ilcDataPopupOpen, setIlcDataPopupOpen] = useState(false);

  // Lightbox state for NC documents
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");
  const [applicationStage, setApplicationStage] = useState("");
  const [assessmentTypeId, setAssessmentTypeId] = useState(null);
  // Router hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedAssessmentId = searchParams.get("assessmentId");

  // Decrypt parameters
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const assessmentId = encryptedAssessmentId
    ? decryptText(encryptedAssessmentId)
    : null;

  // Helper function to format date range for quality assurance measures
  const formatQualityAssuranceDate = (fromDate, toDate) => {
    const formatSingleDate = (dateString) => {
      if (!dateString) return "Not specified";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch (error) {
        return "Invalid date";
      }
    };

    const from = formatSingleDate(fromDate);
    const to = formatSingleDate(toDate);
    if (from === "Not specified" && to === "Not specified") {
      return "Not specified";
    }
    return `${from} to ${to}`;
  };

  const LoadApplicationStage = useCallback(async () => {
    if (!appId) return;
    try {
      const stageResponse = await getApplicationStage(appId);
      if (stageResponse.data.success) {
        setApplicationStage(stageResponse.data.stage);
        setAssessmentTypeId(stageResponse.data.assessmentTypeId);
      }
    } catch (err) {
      console.error("Load application stage error:", err);
    }
  }, [appId]);
  // Load CAB details
  const loadCABDetails = useCallback(async () => {
    if (!appId) {
      setError("Invalid application ID. Please check the URL.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call API to get CAB details
      const response = await DoApiService.getCABDetails({
        appId: parseFloat(appId),
      });

      if (response.success) {
        setCabDetails(response.data);

        // Bind ConsultancyName to advisorConsultant on first load
        if (response.data.ConsultancyName) {
          setFormInputs((prev) => ({
            ...prev,
            advisorConsultant: response.data.ConsultancyName,
          }));
        }
      } else {
        throw new Error(response.message || "Failed to load CAB details");
      }
    } catch (err) {
      setError(`Error loading CAB details: ${err.message}`);
      console.error("Load CAB details error:", err);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  // Load assessor data
  const loadAssessorData = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getAssessorAllotmentDetails9({
        mode: 64,
        appId: parseFloat(appId),
      });

      if (response.success) {
        setAssessorData(response.data || []);
      }
    } catch (err) {
      console.error("Load assessor data error:", err);
      setAssessorData([]);
    }
  }, [appId]);

  // Load selected disciplines
  const loadSelectedDisciplines = useCallback(async () => {
    if (!appId || !orgId) return;

    try {
      const result = await LaboratoryService.getSelectedDisciplines(
        appId,
        orgId,
      );

      if (result.success) {
        setSelectedDisciplines(result.data || []);
      } else {
        throw new Error(
          result.message || "Failed to load selected disciplines",
        );
      }
    } catch (err) {
      console.error("Load selected disciplines error:", err);
      // Don't set error state for disciplines as it's not critical for form display
      setSelectedDisciplines([]);
    }
  }, [appId, orgId]);

  // Load Form 215 report data
  const loadNCReportData = useCallback(async () => {
    if (!appId || !cabDetails?.assessmentId) {
      console.log(
        "⚠️ Missing appId or assessmentId, skipping NC report data load",
      );
      return;
    }

    try {
      // Call the actual Form 215 API
      const response = await fetch(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Form215report?assessmentId=${cabDetails.assessmentId}&appId=${appId}`,
      );
      const data = await response.json();

      if (data.isstatus && response.ok) {
        // Set assessment report data from API response
        setAssessmentReportData({
          assessmentSummary: data.assessmentSummary || "N/A",
          assessmentTeamComment: data.assessmentTeamComment || "N/A",
          recommendationOfAssessmentTeam:
            data.recommendationOfAssessmentTeam || "N/A",
          laboratoryName: data.laboratoryName || "N/A",
          assessmentDate: data.assessmentDate || "N/A",
          assessmentType: data.assessmentType || "N/A",
          laName: data.laName || "N/A",
          nonConformities: data.nonConformities || "No",
          nabL133: data.nabL133 || "N/A",
          nabL142: data.nabL142 || "N/A",
          nabL163: data.nabL163 || "N/A",
          dateto: data.dateto || null,
        });

        // Check if new fields should be displayed
        if (data.dateto) {
          const parseUSDate = (dateString) => {
            if (!dateString) return null;
            const parts = dateString.split(" ")[0].split("/");
            if (parts.length === 3) {
              return new Date(parts[2], parts[0] - 1, parts[1]);
            }
            return null;
          };

          const assessmentEndDate = parseUSDate(data.dateto);
          const targetDate = new Date("2026-05-01");
          if (assessmentEndDate) {
            assessmentEndDate.setHours(0, 0, 0, 0);
            targetDate.setHours(0, 0, 0, 0);
            setIsDisplayNew(assessmentEndDate >= targetDate);
          }
        }
      } else {
        throw new Error(data.message || "Failed to load Form 215 report data");
      }
    } catch (err) {
      console.error("Load Form 215 report data error:", err);
      // Set fallback values if API fails
      setNonConformities({ total: 0 });
      setAssessmentReportData({
        assessmentSummary: "Assessment summary not available",
        assessmentTeamComment: "Assessment team comments not available",
        recommendationOfAssessmentTeam: "Recommendations not available",
        assessmentType: "N/A",
        assessmentDate: "N/A",
        nonConformities: "No",
        laName: "N/A",
      });
    }
  }, [appId, cabDetails?.assessmentId]);

  // Load NAF6 data
  const loadNAF6Data = useCallback(async () => {
    if (!appId || !cabDetails?.assessmentId) {
      console.log("⚠️ Missing appId or assessmentId, skipping NAF6 data load");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/naf6/data-by-assessmentId/${appId}/${cabDetails.assessmentId}`,
      );
      const data = await response.json();

      if (data.success && data.data) {
        setNaf6Data(data.data);

        // If isDisplayNew is true, override formInputs with NAF6 data
        if (isDisplayNew) {
          setFormInputs((prevInputs) => ({
            ...prevInputs,
            trainedAuditor:
              data.data.trainedAuditor || prevInputs.trainedAuditor,
            independenceOfActivity:
              data.data.independenceOfActivity ||
              prevInputs.independenceOfActivity,
            ncsClosed: data.data.ncsClosed || prevInputs.ncsClosed,
            frequencyOfAudit:
              data.data.frequencyOfAudit || prevInputs.frequencyOfAudit,
            allPointsCovered:
              data.data.allPointsCovered || prevInputs.allPointsCovered,
            conductedWithinYear:
              data.data.conductedWithinYear || prevInputs.conductedWithinYear,
            auditComments: data.data.auditComments || prevInputs.auditComments,
            managementComments:
              data.data.managementComments || prevInputs.managementComments,
            ptAvailability:
              data.data.ptAvailability || prevInputs.ptAvailability,
            ptNotAvailableReason:
              data.data.ptNotAvailableReason || prevInputs.ptNotAvailableReason,
            ptCoverageOfScope:
              data.data.ptCoverageOfScope || prevInputs.ptCoverageOfScope,
            ptOutlier: data.data.ptOutlier || prevInputs.ptOutlier,
            ptOutlierRemarks:
              data.data.ptOutlierRemarks || prevInputs.ptOutlierRemarks,
            ilcCoverageOfScope:
              data.data.ilcCoverageOfScope || prevInputs.ilcCoverageOfScope,
            ilcOutlier: data.data.ilcOutlier || prevInputs.ilcOutlier,
            ilcOutlierRemarks:
              data.data.ilcOutlierRemarks || prevInputs.ilcOutlierRemarks,
            otherQualityMeasures:
              data.data.otherQualityMeasures || prevInputs.otherQualityMeasures,
          }));
        }
      }
    } catch (err) {
      console.error("Load NAF6 data error:", err);
    }
  }, [appId, cabDetails?.assessmentId, isDisplayNew]);

  const loadTotalNCReportData = useCallback(async () => {
    if (!appId || !cabDetails?.assessmentId) {
      console.log(
        "⚠️ Missing appId or assessmentId, skipping total NC report data load",
      );
      return;
    }

    try {
      // Call the actual Total NC API
      const response = await fetch(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetNCMainall?assessmentId=${cabDetails.assessmentId}&appId=${appId}`,
      );
      const data = await response.json();

      if (data.isstatus && response.ok) {
        setTotalNc(data.nc.length);
        setNcData(data.nc || []); // Store complete NC array
      } else {
        throw new Error(data.message || "Failed to load Total NC report data");
      }
    } catch (err) {
      console.error("Load Total NC report data error:", err);
    }
  }, [appId, cabDetails?.assessmentId]);

  // Load existing Form11 data
  const loadExistingForm11Data = useCallback(async () => {
    if (!appId) {
      console.log("⚠️ Missing appId, skipping Form11 data load");
      return;
    }

    try {
      setLoadingForm11Data(true);

      const response = await Form11Service.getForm11Data(appId);

      if (response.success && response.data) {
        setExistingForm11Data(response.data);
        setHasExistingData(true);
        setIsFormSaved(true); // Mark as saved since data already exists
        // ✅ FIX HERE
        if (response.data.afterApproval === 1) {
          setShowAfterApprovalBox(true);
          setAfterApprovalRemark(response.data.afterApprovalRemark);
        } else {
          setShowAfterApprovalBox(false);
          setAfterApprovalRemark("");
        }
        // Update form inputs with existing data
        setFormInputs({
          internalAudit: response.data.InternalAudit || "",
          internalAuditType: response.data.InternalAuditType || "",
          trainedAuditor: response.data.TrainedAuditor || "",
          independenceOfActivity: response.data.IndependenceOfActivity || "",
          ncsClosed: response.data.NcsClosed || "",
          effective: response.data.Effective || "",
          frequencyOfAudit: response.data.FrequencyOfAudit || "",
          managementReview: response.data.ManagementReview || "",
          managementReviewType: response.data.ManagementReviewType || "",
          allPointsCovered: response.data.AllPointsCovered || "",
          conductedWithinYear: response.data.ConductedWithinYear || "",
          currentStatus: response.data.currentStatus || "",
          extnValidity: response.data.ExtnValidity || "",
          ptParticipation: response.data.PtIlcRemark
            ? response.data.PtIlcRemark.split(" | ")
                .find((part) => part.startsWith("PT:"))
                ?.replace("PT: ", "") || ""
            : "",
          ptAvailability: response.data.PtAvailability || "",
          ptNotAvailableReason: response.data.PtNotAvailableReason || "",
          ptParticipationType: response.data.PtParticipationType || "",
          ptCoverageOfScope: response.data.PtCoverageOfScope || "",
          ptOutlier: response.data.PtOutlier || "",
          ptOutlierRemarks: response.data.PtOutlierRemarks || "",
          ilcParticipation: response.data.PtIlcRemark
            ? response.data.PtIlcRemark.split(" | ")
                .find((part) => part.startsWith("ILC:"))
                ?.replace("ILC: ", "") || ""
            : "",
          ilcCoverageOfScope: response.data.IlcCoverageOfScope || "",
          ilcOutlier: response.data.IlcOutlier || "",
          ilcOutlierRemarks: response.data.IlcOutlierRemarks || "",
          otherQualityMeasures: response.data.ProcessWitness || "",
          advisorConsultant: response.data.SecratoryObsv
            ? response.data.SecratoryObsv.split(" | ")
                .find((part) => part.startsWith("Advisor/Consultant:"))
                ?.replace("Advisor/Consultant: ", "") || ""
            : "",
          adequacyOrganization: response.data.SecratoryObsv
            ? response.data.SecratoryObsv.split(" | ")
                .find((part) => part.startsWith("Organization Adequacy:"))
                ?.replace("Organization Adequacy: ", "") || ""
            : "",
          otherInformation: response.data.SecratoryObsv
            ? response.data.SecratoryObsv.split(" | ")
                .find((part) => part.startsWith("Other Information:"))
                ?.replace("Other Information: ", "") || ""
            : "",
          observationRecommendation: response.data.SecratoryObsv
            ? response.data.SecratoryObsv.split(" | ")
                .find((part) =>
                  part.startsWith("Observations & Recommendations:"),
                )
                ?.replace("Observations & Recommendations: ", "") || ""
            : "",
          acProcessComments: response.data.ACComment || "",
          auditComments: response.data.AuditComments || "",
          managementComments: response.data.ManagementComments || "",
        });

        // Update facility types if exists
        if (response.data.Facility) {
          const facilityValue = response.data.Facility.toLowerCase();

          setFacilityTypes({
            PermanentFacility: facilityValue.includes("permanent facility"),
            SiteFacility: facilityValue.includes("site facility"),
            MobileFacility: facilityValue.includes("mobile facility"),
          });
        }

        if (
          response.data.DecisionProcess &&
          response.data.DecisionProcess !== null &&
          response.data.DecisionProcess !== ""
        ) {
          let decisionValue;
          // Handle different possible values from API
          const apiDecision = response.data.DecisionProcess.toString()
            .toLowerCase()
            .trim();

          if (
            apiDecision === "internal" ||
            apiDecision === "internal_approval" ||
            apiDecision === "through internal approval"
          ) {
            decisionValue = "internal_approval";
          } else if (
            apiDecision === "acm" ||
            apiDecision === "external" ||
            apiDecision === "through acm"
          ) {
            decisionValue = "acm";
          } else {
            decisionValue = response.data.DecisionProcess; // Use as-is if doesn't match expected values
          }
          setDecisionProcess(decisionValue);
        } else {
          setDecisionProcess(""); // Reset to empty if no value
        }
      } else {
        setExistingForm11Data(null);
        setHasExistingData(false);
      }
    } catch (err) {
      console.error("Load existing Form11 data error:", err);
      setExistingForm11Data(null);
      setHasExistingData(false);
    } finally {
      setLoadingForm11Data(false);
    }
  }, [appId]);

  // Load additional documents
  const loadAdditionalDocuments = useCallback(async () => {
    if (!appId || !orgId || !cabDetails?.assessmentId) {
      console.log(
        "⚠️ Missing required parameters for additional documents, skipping load",
      );
      return;
    }

    try {
      setLoadingDocuments(true);
      const response = await additionalDocumentService.getDocuments({
        orgId: orgId,
        appId: appId,
        targetType: "assessor",
        assessmentId: cabDetails.assessmentId,
      });

      if (response.success) {
        setAdditionalDocuments(response.data || []);
      } else {
        setAdditionalDocuments([]);
      }
    } catch (err) {
      console.error("Load additional documents error:", err);
      setAdditionalDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [appId, orgId, cabDetails?.assessmentId]);

  // Load PT Provider data for popup (only PT data)
  const loadPTData = useCallback(async () => {
    try {
      setPtDataLoading(true);
      setPtDataError(null);
      console.log("Loading PT data with appId:", appId, "orgId:", orgId);

      const response = await PTProviderService.getPTProviderData(appId, orgId);

      if (response.success) {
        // Filter only PT data (includes PT and PT/EQA types)
        const filteredPTData = (response.data || []).filter(
          (item) =>
            item.participationIn === "PT" ||
            item.participationIn === "PT/EQA (NABL Accredited)" ||
            item.participationIn === "PT/EQA (Others)" ||
            item.participationIn?.toLowerCase().includes("pt"),
        );
        setPtData(filteredPTData);
        console.log(
          "PT data loaded successfully:",
          filteredPTData.length,
          "PT records",
        );
      } else {
        setPtData([]);
        console.warn("PT data API returned success: false");
      }
    } catch (error) {
      console.error("Error loading PT provider data:", error);
      setPtDataError("Failed to load PT data. Please try again.");
      setPtData([]);
    } finally {
      setPtDataLoading(false);
    }
  }, [appId, orgId]);

  // Load ILC data for popup (only ILC data)
  const loadILCData = useCallback(async () => {
    try {
      setIlcDataLoading(true);
      setIlcDataError(null);
      console.log("Loading ILC data with appId:", appId, "orgId:", orgId);

      const response = await PTProviderService.getPTProviderData(appId, orgId);

      if (response.success) {
        // Filter only ILC data
        const filteredILCData = (response.data || []).filter(
          (item) => item.participationIn === "ILC",
        );
        setIlcData(filteredILCData);
        console.log(
          "ILC data loaded successfully:",
          filteredILCData.length,
          "ILC records",
        );
      } else {
        setIlcData([]);
        console.warn("ILC data API returned success: false");
      }
    } catch (error) {
      console.error("Error loading ILC data:", error);
      setIlcDataError("Failed to load ILC data. Please try again.");
      setIlcData([]);
    } finally {
      setIlcDataLoading(false);
    }
  }, [appId, orgId]);

  // Load data when component mounts
  useEffect(() => {
    loadCABDetails();
    loadAssessorData();
    loadSelectedDisciplines();
    loadExistingForm11Data();
    LoadApplicationStage();
  }, [
    loadCABDetails,
    loadAssessorData,
    loadSelectedDisciplines,
    loadExistingForm11Data,
    LoadApplicationStage,
  ]);

  // Debug: Watch for decisionProcess state changes
  useEffect(() => {
    console.log("🎯 Decision Process state changed to:", decisionProcess);
  }, [decisionProcess]);

  // Load NC report data and additional documents after CAB details are loaded
  useEffect(() => {
    if (cabDetails?.assessmentId) {
      loadNCReportData();
      loadTotalNCReportData();
      loadAdditionalDocuments();
    }
  }, [
    cabDetails?.assessmentId,
    loadNCReportData,
    loadTotalNCReportData,
    loadAdditionalDocuments,
  ]);

  // Load NAF6 data when isDisplayNew changes
  // useEffect(() => {
  //   if (isDisplayNew && cabDetails?.assessmentId) {
  //     loadNAF6Data();
  //   }
  // }, [isDisplayNew, cabDetails?.assessmentId, loadNAF6Data]);

  // Update facility types when cabDetails change
  useEffect(() => {
    if (cabDetails) {
      setFacilityTypes({
        PermanentFacility:
          cabDetails.PermanentFacility === true ||
          cabDetails.PermanentFacility === "true",
        SiteFacility:
          cabDetails.SiteFacility === true ||
          cabDetails.SiteFacility === "true",
        MobileFacility:
          cabDetails.MobileFacility === true ||
          cabDetails.MobileFacility === "true",
      });
    }
  }, [cabDetails]);

  // Handle facility type change
  const handleFacilityChange = (facilityType) => (event) => {
    const newValue = event.target.checked;
    setFacilityTypes((prev) => ({
      ...prev,
      [facilityType]: newValue,
    }));

    // You can add API call here to save the changes
    // await DoApiService.updateFacilityTypes(appId, { ...facilityTypes, [facilityType]: newValue });
  };

  // Handle form input changes
  const handleInputChange = (fieldName) => (event) => {
    const value = event.target.value;
    setFormInputs((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const validateRequiredFields = () => {
    const missing = [];
    if (!decisionProcess || decisionProcess.trim() === "")
      missing.push("Decision Process");
    if (
      !formInputs.observationRecommendation ||
      formInputs.observationRecommendation.trim() === ""
    )
      missing.push("Observations & Recommendations");
    return missing;
  };

  // Handle form submission
  const handleSubmitForm = async () => {
    const missing = validateRequiredFields();
    if (missing.length > 0) {
      setSubmitError(`Please fill required fields: ${missing.join(", ")}`);
      return;
    }
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      const uniqueDates = [
        ...new Set(cabDetails?.selfi?.map((x) => x.dateFrom)),
      ];
      // Extract form data from current component state
      const formData = Form11Service.extractFormDataFromState({
        cabDetails,
        selectedDisciplines,
        assessorData,
        facilityTypes,
        assessmentReportData,
        formInputs: {
          ...formInputs,
          decisionProcess: decisionProcess,
          totalNc: TotalNc || 0,
        },
      });

      // Add additional required fields including new Quality Assurance fields
      const preparedData = Form11Service.prepareFormDataForSubmission(
        formData,
        {
          appId: parseInt(appId),
          orgId: localStorage.getItem("orgId"),
          insertedBy: "DO_User", // You can get this from user context
          insertIp: window.location.hostname,
          // New Quality Assurance fields
          trainedAuditor: formInputs.trainedAuditor,
          independenceOfActivity: formInputs.independenceOfActivity,
          ncsClosed: formInputs.ncsClosed,
          effective: formInputs.effective,
          currentStatus: formInputs.currentStatus,
          extnValidity: formInputs.extnValidity,
          frequencyOfAudit: formInputs.frequencyOfAudit,
          allPointsCovered: formInputs.allPointsCovered,
          conductedWithinYear: formInputs.conductedWithinYear,
          ptAvailability: formInputs.ptAvailability,
          ptNotAvailableReason: formInputs.ptNotAvailableReason,
          ptCoverageOfScope: formInputs.ptCoverageOfScope,
          ptOutlier: formInputs.ptOutlier,
          ptOutlierRemarks: formInputs.ptOutlierRemarks,
          ilcCoverageOfScope: formInputs.ilcCoverageOfScope,
          ilcOutlier: formInputs.ilcOutlier,
          ilcOutlierRemarks: formInputs.ilcOutlierRemarks,
          otherQualityMeasures: formInputs.otherQualityMeasures,
          auditComments: formInputs.auditComments,
          managementComments: formInputs.managementComments,
        },
      );
      preparedData.assessmentDates = uniqueDates.join("; ");
      // Submit to API
      const result = await Form11Service.insertForm11Data(preparedData);

      if (result.success) {
        setSubmitSuccess(true);
        setIsFormSaved(true); // Mark form as saved to show Forward to JD button

        // Show success message for 2 seconds instead of navigating away
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 2000);
      } else {
        throw new Error(result.error || "Failed to submit Form11 data");
      }
    } catch (error) {
      console.error("❌ Error submitting Form11:", error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateForm = async () => {
    const missing = validateRequiredFields();
    if (missing.length > 0) {
      setSubmitError(`Please fill required fields: ${missing.join(", ")}`);
      return;
    }
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      const uniqueDates = [
        ...new Set(cabDetails?.selfi?.map((x) => x.dateFrom)),
      ];

      // Extract form data from current component state
      const formData = Form11Service.extractFormDataFromState({
        cabDetails,
        selectedDisciplines,
        assessorData,
        facilityTypes,
        assessmentReportData,
        formInputs: {
          ...formInputs,
          decisionProcess: decisionProcess,
          totalNc: TotalNc || 0,
        },
      });

      // Add additional required fields including new Quality Assurance fields
      const preparedData = Form11Service.prepareFormDataForSubmission(
        formData,
        {
          appId: parseInt(appId),
          orgId: localStorage.getItem("orgId"),
          insertedBy: "DO_User", // You can get this from user context
          insertIp: window.location.hostname,
          // New Quality Assurance fields
          trainedAuditor: formInputs.trainedAuditor,
          independenceOfActivity: formInputs.independenceOfActivity,
          ncsClosed: formInputs.ncsClosed,
          effective: formInputs.effective,
          currentStatus: formInputs.currentStatus,
          extnValidity: formInputs.extnValidity,
          frequencyOfAudit: formInputs.frequencyOfAudit,
          allPointsCovered: formInputs.allPointsCovered,
          conductedWithinYear: formInputs.conductedWithinYear,
          ptAvailability: formInputs.ptAvailability,
          ptNotAvailableReason: formInputs.ptNotAvailableReason,
          ptCoverageOfScope: formInputs.ptCoverageOfScope,
          ptOutlier: formInputs.ptOutlier,
          ptOutlierRemarks: formInputs.ptOutlierRemarks,
          ilcCoverageOfScope: formInputs.ilcCoverageOfScope,
          ilcOutlier: formInputs.ilcOutlier,
          ilcOutlierRemarks: formInputs.ilcOutlierRemarks,
          processWitness: formInputs.otherQualityMeasures,
          auditComments: formInputs.auditComments,
          managementComments: formInputs.managementComments,
        },
      );
      preparedData.assessmentDates = uniqueDates.join("; ");

      // Submit to API
      const result = await Form11Service.insertForm11Data(preparedData);

      if (result.success) {
        setSubmitSuccess(true);
        setIsFormSaved(true); // Mark form as saved to show Forward to JD button

        // Show success message for 2 seconds instead of navigating away
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 2000);
      } else {
        throw new Error(result.error || "Failed to submit Form11 data");
      }
    } catch (error) {
      console.error("❌ Error submitting Form11:", error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // REPLACE YOUR FUNCTION handleUpdateFormAfterApproval

  const handleUpdateFormAfterApproval = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      const uniqueDates = [
        ...new Set(cabDetails?.selfi?.map((x) => x.dateFrom)),
      ];

      const formData = Form11Service.extractFormDataFromState({
        cabDetails,
        selectedDisciplines,
        assessorData,
        facilityTypes,
        assessmentReportData,
        formInputs: {
          ...formInputs,
          decisionProcess,
          totalNc: TotalNc || 0,
        },
      });

      const preparedData = Form11Service.prepareFormDataForSubmission(
        formData,
        {
          appId: parseInt(appId),
          orgId: localStorage.getItem("orgId"),
          insertedBy: "DO_User",
          insertIp: window.location.hostname,

          trainedAuditor: formInputs.trainedAuditor,
          independenceOfActivity: formInputs.independenceOfActivity,
          ncsClosed: formInputs.ncsClosed,
          effective: formInputs.effective,
          currentStatus: formInputs.currentStatus,
          extnValidity: formInputs.extnValidity,
          frequencyOfAudit: formInputs.frequencyOfAudit,
          allPointsCovered: formInputs.allPointsCovered,
          conductedWithinYear: formInputs.conductedWithinYear,
          ptAvailability: formInputs.ptAvailability,
          ptNotAvailableReason: formInputs.ptNotAvailableReason,
          ptCoverageOfScope: formInputs.ptCoverageOfScope,
          ptOutlier: formInputs.ptOutlier,
          ptOutlierRemarks: formInputs.ptOutlierRemarks,
          ilcCoverageOfScope: formInputs.ilcCoverageOfScope,
          ilcOutlier: formInputs.ilcOutlier,
          ilcOutlierRemarks: formInputs.ilcOutlierRemarks,
          processWitness: formInputs.otherQualityMeasures,
          auditComments: formInputs.auditComments,
          managementComments: formInputs.managementComments,

          // NEW FIELD
          afterApprovalRemark: afterApprovalRemark,
        },
      );

      preparedData.assessmentDates = uniqueDates.join("; ");

      const result = await Form11Service.insertForm11Data(preparedData);

      if (result.success) {
        setSubmitSuccess(true);
        setShowAfterApprovalBox(false);

        setTimeout(() => {
          setSubmitSuccess(false);
        }, 2000);
      } else {
        throw new Error(result.error || "Failed to update");
      }
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Edit button click
  const handleEditForm = () => {
    setIsEditMode(true);
    setUpdateError(null);
  };
  const handleEditFormAfterApprove = () => {
    setIsEditModeAfterUpdate(true);
    setShowAfterApprovalBox(true);
    setDisabledApprovalBox(true);
    setUpdateError(null);
  };

  // Handle Cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setIsEditModeAfterUpdate(false);
    setDisabledApprovalBox(false);
    setUpdateError(null);
    // Optionally reload the original data
    loadExistingForm11Data();
  };

  // Handle Forward to JD button click (shows confirmation)
  const handleForwardToJD = () => {
    const missing = validateRequiredFields();
    if (missing.length > 0) {
      setForwardError(`Please fill required fields before forwarding: ${missing.join(", ")}`);
      return;
    }
    setShowForwardConfirm(true);
  };

  // Handle Forward to JD confirmation
  const handleForwardConfirm = async () => {
    try {
      setIsForwarding(true);
      setForwardError(null);
      setShowForwardConfirm(false);

      const forwardData = {
        appId: parseInt(appId),
        orgId: parseInt(localStorage.getItem("orgId") || orgId),
      };

      // Call the forward to JD API
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/form11/forward-to-jd`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(forwardData),
        },
      );

      const result = await response.json();

      if (result.success) {
        setForwardSuccess(true);

        // Show success message and navigate back after 3 seconds
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      } else {
        throw new Error(result.message || "Failed to forward to JD");
      }
    } catch (error) {
      console.error("❌ Error forwarding to JD:", error);
      setForwardError(error.message);
    } finally {
      setIsForwarding(false);
    }
  };

  // Handle Forward to JD confirmation cancel
  const handleForwardCancel = () => {
    setShowForwardConfirm(false);
  };

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1);
  };

  // Handle NC report view navigation
  const handleViewNCReport = () => {
    // Create encrypted parameters for the NC report URL
    const encryptedAppIdParam = encryptedAppId; // Use the existing encrypted appId
    const ncReportUrl = `/main/do/nc-report?appId=${encryptAppId(appId)}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
    // Open in new tab
    window.open(ncReportUrl, "_blank", "noopener,noreferrer");
  };
  const handleViewwitnessReport = () => {
    // Create encrypted parameters for the witness report URL
    const encryptedAppIdParam = encryptedAppId; // Use the existing encrypted appId
    const witnessReportUrl = `/main/do/FormTestWitness?appId=${encryptAppId(appId)}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
    // Open in new tab
    window.open(witnessReportUrl, "_blank", "noopener,noreferrer");
  };

  const handleTestWitnessByConclusion = () => {
    const encryptedAppIdParam = encryptedAppId;
    const witnessReportUrl = `/main/do/testWitnessReport?appId=${encryptAppId(appId)}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
    window.open(witnessReportUrl, "_blank", "noopener,noreferrer");
  };

  const handleViewScopeReport = () => {
    // Create encrypted parameters for the scope report URL
    const scopeReportUrl = `/main/do/form72?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}`;
    // Open in new tab
    window.open(scopeReportUrl, "_blank", "noopener,noreferrer");
  };
  const handleView73Report = () => {
    // Create encrypted parameters for the scope report URL
    const scopeReportUrl = `/main/do/form73?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&observation=${encryptText("true")}&assessmentId=${encryptText(assessmentId)}`;
    // Open in new tab
    window.open(scopeReportUrl, "_blank", "noopener,noreferrer");
  };

  // Handle view profile action
  const handleViewProfile = (assessor) => {
    const profileUrl = `/main/assessor-profile?AssessorId=${encryptText(assessor.allotedToId)}`;
    window.open(profileUrl, "_blank", "noopener,noreferrer");
  };

  // Handle viewing additional documents
  const handleViewDocument = async (document) => {
    if (document.fileKey || document.fileName) {
      const success = await s3Utils.handleFileViewWithS3(
        document.fileKey || document.fileName,
        {
          showError: (msg) => setError(msg),
          showLoading: () => setLoading(true),
          hideLoading: () => setLoading(false),
        },
      );

      if (!success) {
        setError("Failed to open document");
      }
    }
  };

  // Handle PT data popup
  const handleViewPTData = async () => {
    setPtDataPopupOpen(true);
    await loadPTData();
  };

  const handleClosePTDataPopup = () => {
    setPtDataPopupOpen(false);
    setPtDataError(null);
  };

  // Handle ILC data popup
  const handleViewILCData = async () => {
    setIlcDataPopupOpen(true);
    await loadILCData();
  };

  const handleCloseILCDataPopup = () => {
    setIlcDataPopupOpen(false);
    setIlcDataError(null);
  };

  // Helper function to get provider name based on participation type
  const getProviderName = (
    participationIn,
    nodalLaboratory,
    nodalLaboratoryOthers,
  ) => {
    if (participationIn === "PT" && nodalLaboratory) {
      return nodalLaboratory;
    }
    if (participationIn === "PT/EQA (Others)" && nodalLaboratoryOthers) {
      return nodalLaboratoryOthers;
    }
    return "N/A";
  };

  // Handle Internal Audit Document viewing
  const handleViewInternalAuditDocument = async () => {
    if (!cabDetails?.InternalAuditDocument) {
      setError("Internal Audit Document not found");
      return;
    }

    try {
      setLoading(true);
      const success = await s3Utils.handleFileViewWithS3(
        cabDetails.InternalAuditDocument,
        {
          showError: (msg) => setError(msg),
          showLoading: () => setLoading(true),
          hideLoading: () => setLoading(false),
        },
      );

      if (!success) {
        setError("Failed to open Internal Audit Document");
      }
    } catch (error) {
      console.error("Error opening Internal Audit Document:", error);
      setError("Failed to open Internal Audit Document");
    } finally {
      setLoading(false);
    }
  };

  // Handle Management Review Document viewing
  const handleViewManagementReviewDocument = async () => {
    if (!cabDetails?.ManagementReviewDocument) {
      setError("Management Review Document not found");
      return;
    }

    try {
      setLoading(true);
      const success = await s3Utils.handleFileViewWithS3(
        cabDetails.ManagementReviewDocument,
        {
          showError: (msg) => setError(msg),
          showLoading: () => setLoading(true),
          hideLoading: () => setLoading(false),
        },
      );

      if (!success) {
        setError("Failed to open Management Review Document");
      }
    } catch (error) {
      console.error("Error opening Management Review Document:", error);
      setError("Failed to open Management Review Document");
    } finally {
      setLoading(false);
    }
  };

  // Handle QMS Document viewing
  const handleViewQMSDocument = async () => {
    if (!cabDetails?.QMSFile) {
      setError("QMS Document not found");
      return;
    }

    try {
      setLoading(true);
      const success = await s3Utils.handleFileViewWithS3(cabDetails.QMSFile, {
        showError: (msg) => setError(msg),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
      });

      if (!success) {
        setError("Failed to open QMS Document");
      }
    } catch (error) {
      console.error("Error opening QMS Document:", error);
      setError("Failed to open QMS Document");
    } finally {
      setLoading(false);
    }
  };

  // Get target type display name
  const getTargetTypeDisplay = (type) => {
    switch (type) {
      case "lab":
        return "Laboratory";
      case "assessor":
        return "Assessor";
      case "do":
        return "NABL";
      default:
        return type;
    }
  };

  // Get target type color
  const getTargetTypeColor = (type) => {
    switch (type) {
      case "lab":
        return "primary";
      case "assessor":
        return "secondary";
      case "do":
        return "info";
      default:
        return "default";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Mock disciplines data (you can replace this with actual API data)
  const existingDisciplines =
    selectedDisciplines.length > 0
      ? selectedDisciplines.map(
          (discipline) =>
            discipline.disciplineName ||
            discipline.name ||
            discipline.title ||
            "Unknown Discipline",
        )
      : [
          "Clinical Biochemistry",
          "Clinical Pathology",
          "Histopathology",
          "Cytopathology",
          "Haematology",
          "Microbiology & Infectious disease serology",
          "Molecular Testing",
        ];

  // Get capacity color for chip display
  const getCapacityColor = (capacity) => {
    const colors = {
      "Lead Assessor": "primary",
      "Technical Assessor": "success",
      Observer: "secondary",
      "Technical Expert": "warning",
      "Team Leader": "error",
    };
    return colors[capacity] || "default";
  };

  // Colors object for document components
  const colors = {
    primary: { main: "#1976d2" },
    grey: { 100: "#f5f5f5", 300: "#e0e0e0", 700: "#616161" },
    error: { main: "#d32f2f" },
    success: { main: "#2e7d32" },
  };

  // Handle lightbox for images
  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  // Single Document Component
  const SingleDocument = ({
    doc,
    index,
    isPdf,
    isExcel,
    colors,
    onImageClick,
  }) => {
    const [imageUrl, setImageUrl] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadDocument = async () => {
        try {
          setLoading(true);
          if (doc) {
            // Check if it's just a filename or full URL
            if (s3Utils.isJustFilename(doc)) {
              const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
              setImageUrl(signedUrl);
            } else {
              setImageUrl(doc);
            }
          }
        } catch (error) {
          console.error("Error loading document:", error);
          setImageUrl("");
        } finally {
          setLoading(false);
        }
      };

      loadDocument();
    }, [doc]);

    if (loading) {
      return (
        <Box
          sx={{
            width: 80,
            height: 80,
            border: `2px solid ${colors.grey[300]}`,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: colors.grey[100],
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      );
    }

    // For PDF/Excel files, show thumbnail with icon
    if (isPdf || isExcel) {
      return (
        <Box
          sx={{
            width: 80,
            height: 80,
            border: `2px solid ${colors.grey[300]}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
            bgcolor: isPdf
              ? alpha(colors.error.main, 0.05)
              : alpha(colors.success.main, 0.05),
            "&:hover": {
              borderColor: isPdf ? colors.error.main : colors.success.main,
              transform: "scale(1.05)",
              boxShadow: 3,
            },
          }}
          onClick={() => window.open(imageUrl, "_blank")}
        >
          {isPdf ? (
            <PictureAsPdfIcon
              sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
            />
          ) : (
            <DescriptionIcon
              sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
            />
          )}
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
          >
            {isPdf ? "PDF" : "Excel"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.40rem" }}
          >
            Click to view
          </Typography>
        </Box>
      );
    }

    // For images, show the actual image
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: colors.primary.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() =>
          onImageClick
            ? onImageClick(imageUrl)
            : window.open(imageUrl, "_blank")
        }
      >
        <img
          src={imageUrl}
          alt={`Evidence ${index + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.src =
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
          }}
        />
      </Box>
    );
  };

  // Working Space Document Component
  const WorkingSpaceDocument = ({
    doc,
    index,
    isImage,
    isPdf,
    isExcel,
    colors,
    onImageClick,
  }) => {
    // Check if doc contains multiple files separated by ~
    if (doc && doc.includes("~")) {
      const files = doc.split("~");
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {files.map((file, fileIndex) => {
            const cleanFile = file.trim();
            if (!cleanFile) return null;

            const fileIsPdf = cleanFile.toLowerCase().endsWith(".pdf");
            const fileIsExcel = cleanFile.toLowerCase().match(/\.(xls|xlsx)$/);

            return (
              <Box
                key={fileIndex}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary.main,
                    minWidth: 50,
                  }}
                >
                  Doc {fileIndex + 1}:
                </Typography>
                <SingleDocument
                  doc={cleanFile}
                  index={fileIndex}
                  isPdf={fileIsPdf}
                  isExcel={fileIsExcel}
                  colors={colors}
                  onImageClick={onImageClick}
                />
              </Box>
            );
          })}
        </Box>
      );
    }

    // Single file - use the original logic
    return (
      <SingleDocument
        doc={doc}
        index={index}
        isPdf={isPdf}
        isExcel={isExcel}
        colors={colors}
        onImageClick={onImageClick}
      />
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Form 11...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  // Helper variable to determine if fields should be disabled
  const fieldsDisabled = hasExistingData && !isEditMode; // Disabled only when data exists and not in edit mode

  // NAF6 fields should always be disabled when isDisplayNew is true
  const naf6FieldsDisabled = isDisplayNew || fieldsDisabled;

  // Helper functions for conditional styling and text
  const getFieldPlaceholder = (defaultText) => {
    return fieldsDisabled ? "Click 'Edit Form 11' to modify" : defaultText;
  };

  const getFieldBackgroundColor = (
    editableColor = "#fafafa",
    disabledColor = "#fafafa",
  ) => {
    return fieldsDisabled ? disabledColor : editableColor;
  };

  const handleViewNAF6 = () => {
    const naf6Url = `/main/view-NAF6?appId=${encryptAppId(appId)}&assessmentId=${encryptText(cabDetails?.assessmentId)}`;
    window.open(naf6Url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        pt: 2,
        pb: 4,
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{
              mb: 2,
              "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBackClick}
              sx={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { color: "white" },
              }}
            >
              Dashboard
            </Link>
            <Typography variant="body2" sx={{ color: "white" }}>
              Form 11 - Assessment Summary
            </Typography>
          </Breadcrumbs>

          {/* Header Content */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  mr: 2,
                  width: 56,
                  height: 56,
                }}
              >
                <DescriptionIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Assessment Summary - Form 11
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {cabDetails?.applicationNo || "N/A"} •{" "}
                  {cabDetails?.labType || "N/A"}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ px: 3 }}>
        {/* CAB Information Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{ bgcolor: "primary.main", mr: 2, width: 40, height: 40 }}
                >
                  <BusinessIcon />
                </Avatar>

                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  CAB Information
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewNAF6}
              >
                View NAF6
              </Button>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    CAB Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.labName || cabDetails?.laboratoryName || "N/A"}
                    ,{cabDetails?.cityName || cabDetails?.cityName || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    CAB ID.
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.applicationNo || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Field
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.labType || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Type of Assessment
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.applicationType || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Date of receipt of application
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(cabDetails?.submissionDate) || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Standard
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    ISO 15189:{cabDetails?.standard || "2022"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Premises assessed
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cabDetails?.AddressLine1 ||
                      cabDetails?.AddressLine2 ||
                      "N/A"}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    State:{" "}
                    {cabDetails?.stateName || cabDetails?.stateName || "N/A"}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    City:{" "}
                    {cabDetails?.cityName || cabDetails?.cityName || "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Validity / Current status of accreditation
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    value={formInputs.currentStatus}
                    onChange={handleInputChange("currentStatus")}
                    inputProps={{
                      readOnly: fieldsDisabled,
                    }}
                    placeholder={getFieldPlaceholder("Enter Current Status")}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Extension of validity (if applicable)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    value={formInputs.extnValidity}
                    onChange={handleInputChange("extnValidity")}
                    inputProps={{
                      readOnly: fieldsDisabled,
                    }}
                    placeholder={getFieldPlaceholder(
                      "Enter Extension of validity (if applicable)",
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Disciplines Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{ bgcolor: "success.main", mr: 2, width: 40, height: 40 }}
              >
                <AssignmentIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Discipline(s)
              </Typography>
            </Box>

            {/* Existing Disciplines */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              (a) Existing{" "}
              {selectedDisciplines.length > 0 &&
                `(${selectedDisciplines.length} found)`}
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>S No.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell>
                    {selectedDisciplines.length > 0 &&
                      selectedDisciplines[0]?.groupName && (
                        <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                      )}
                    {selectedDisciplines.length > 0 &&
                      selectedDisciplines[0]?.facilityName && (
                        <TableCell sx={{ fontWeight: 600 }}>Facility</TableCell>
                      )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedDisciplines.length > 0 ? (
                    selectedDisciplines.map((discipline, index) => (
                      <TableRow key={discipline.id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {discipline.DisciplineName ||
                            discipline.name ||
                            discipline.title ||
                            "N/A"}
                        </TableCell>
                        {discipline.groupName && (
                          <TableCell>{discipline.groupName}</TableCell>
                        )}
                        {discipline.facilityName && (
                          <TableCell>{discipline.facilityName}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : existingDisciplines.length > 0 ? (
                    existingDisciplines.map((discipline, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{discipline}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{
                          textAlign: "center",
                          fontStyle: "italic",
                          color: "text.secondary",
                        }}
                      >
                        No disciplines found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Scope Extension */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              (b) Scope Extension, (if any)
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>S No.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Facility</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        textAlign: "center",
                        fontStyle: "italic",
                        color: "text.secondary",
                      }}
                    >
                      No scope extension requested
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Facility Section - View Only */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Facility Types
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                {/* Permanent Facility - Show only if true in API */}
                {facilityTypes.PermanentFacility && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "primary.main",
                        borderRadius: 2,
                        backgroundColor: "primary.50",
                        opacity: 0.8,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={true}
                            disabled={true}
                            color="primary"
                            icon={<HomeIcon />}
                            checkedIcon={<HomeIcon />}
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            Permanent Facility
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                )}

                {/* Site Facility - Show only if true in API */}
                {facilityTypes.SiteFacility && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "secondary.main",
                        borderRadius: 2,
                        backgroundColor: "secondary.50",
                        opacity: 0.8,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={true}
                            disabled={true}
                            color="secondary"
                            icon={<LocationCityIcon />}
                            checkedIcon={<LocationCityIcon />}
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            Site Facility
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                )}

                {/* Mobile Facility - Show only if true in API */}
                {facilityTypes.MobileFacility && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "success.main",
                        borderRadius: 2,
                        backgroundColor: "success.50",
                        opacity: 0.8,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={true}
                            disabled={true}
                            color="success"
                            icon={<DirectionsCarIcon />}
                            checkedIcon={<DirectionsCarIcon />}
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            Mobile Facility
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                )}

                {/* Show message if no facilities are available */}
                {!facilityTypes.PermanentFacility &&
                  !facilityTypes.SiteFacility &&
                  !facilityTypes.MobileFacility && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 3,
                          border: 1,
                          borderColor: "grey.300",
                          borderRadius: 2,
                          backgroundColor: "grey.50",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                          No facility types found in the application data
                        </Typography>
                      </Box>
                    </Grid>
                  )}
              </Grid>
            </Box>
          </CardContent>
        </Card>

        {/* Assessment Team Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{ bgcolor: "info.main", mr: 2, width: 40, height: 40 }}
              >
                <PeopleIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Assessment Team
              </Typography>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>S. No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Laboratory Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Assessor Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expertise</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Assessment Dates
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessorData && assessorData.length > 0 ? (
                    assessorData.map((assessor, index) => (
                      <TableRow key={assessor.id || index}>
                        <TableCell>
                          <Box sx={{ textAlign: "center" }}>
                            {index + 1} <br />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewProfile(assessor)}
                              sx={{ ml: 1 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "small" }}
                          >
                            {assessor.laboratoryName || "N/A"}
                          </Typography>
                          <Chip
                            label={assessor.applicationNo || "N/A"}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium", mb: 0.5 }}
                          >
                            {assessor.assessorName || "N/A"}
                          </Typography>
                          <Chip
                            sx={{ mb: 0.5 }}
                            label={
                              assessor.capacity ||
                              assessor.assessorCapacity ||
                              "Technical Assessor"
                            }
                            size="small"
                            color={getCapacityColor(
                              assessor.capacity || assessor.assessorCapacity,
                            )}
                            variant="outlined"
                          />
                          <Chip
                            label={assessor.loginName || "N/A"}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {assessor.fieldOfExpertise ||
                              assessor.expertise ||
                              "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {assessor.organisation ||
                              assessor.organization ||
                              "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "success.main",
                                fontSize: "0.75rem",
                              }}
                            >
                              From:{" "}
                              {formatDate(
                                assessor.fromDate || assessor.startDate,
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "error.main", fontSize: "0.75rem" }}
                            >
                              To:{" "}
                              {formatDate(assessor.toDate || assessor.endDate)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography variant="body1" color="text.secondary">
                          No assessment team data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Assessment Results Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{ bgcolor: "warning.main", mr: 2, width: 40, height: 40 }}
              >
                <AssessmentIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Observations/ Comments of the Assessment team
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Observations/Comments */}

              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: "#fff3e0", borderRadius: 2, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Test/ Calibration/ PT Scheme /RMP process witnessed
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleViewwitnessReport}
                    sx={{
                      borderColor: "warning.main",
                      color: "warning.main",
                      "&:hover": {
                        borderColor: "warning.dark",
                        backgroundColor: "warning.50",
                      },
                    }}
                  >
                    View Details
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleTestWitnessByConclusion}
                    sx={{
                      ml: 2,
                      borderColor: "warning.main",
                      color: "warning.main",
                      "&:hover": {
                        borderColor: "warning.dark",
                        backgroundColor: "warning.50",
                      },
                    }}
                  >
                    Test Witness by Conclusion
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: "#e8f5e8", borderRadius: 2, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Present status of Non Conformities
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleViewNCReport}
                    sx={{
                      borderColor: "success.main",
                      color: "success.main",
                      "&:hover": {
                        borderColor: "success.dark",
                        backgroundColor: "success.50",
                      },
                    }}
                  >
                    View Details
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{ p: 2, bgcolor: "#ebfffdff", borderRadius: 2, mb: 2 }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Recommended Scope (Form72):
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleViewScopeReport}
                    sx={{
                      borderColor: "primary.main",
                      color: "primary.main",
                      "&:hover": {
                        borderColor: "primary.dark",
                        backgroundColor: "primary.50",
                      },
                    }}
                  >
                    View Details
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ p: 2, bgcolor: "#ebf1ffff", borderRadius: 2, mb: 2 }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Person declared to report, review and authorized the result:
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleView73Report}
                    sx={{
                      borderColor: "primary.main",
                      color: "primary.main",
                      "&:hover": {
                        borderColor: "primary.dark",
                        backgroundColor: "primary.50",
                      },
                    }}
                  >
                    View Details
                  </Button>
                </Box>
              </Grid>
              {/* Non-Conformities */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: "#ffebee", borderRadius: 2, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Total No. of Non-Conformities: {TotalNc || 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Quality Assurance Measures Section */}
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Quality Assurance Measures
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {/* Internal Audit */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Internal Audit
                  </Typography>
                  {cabDetails?.InternalAuditFromDate ||
                  cabDetails?.InternalAuditToDate ? (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "#1976d2", mb: 0.5 }}
                          >
                            📅 Internal Audit Dates:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#333" }}>
                            {formatQualityAssuranceDate(
                              cabDetails.InternalAuditFromDate,
                              cabDetails.InternalAuditToDate,
                            )}
                          </Typography>
                        </Box>
                        {cabDetails?.InternalAuditDocument && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={handleViewInternalAuditDocument}
                            sx={{
                              borderColor: "#1976d2",
                              color: "#1976d2",
                              "&:hover": {
                                borderColor: "#1565c0",
                                backgroundColor: "#e3f2fd",
                              },
                            }}
                          >
                            View Document
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : cabDetails?.InternalAuditDocument ? (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#1976d2" }}
                        >
                          📄 Internal Audit Document Available
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewInternalAuditDocument}
                          sx={{
                            borderColor: "#1976d2",
                            color: "#1976d2",
                            "&:hover": {
                              borderColor: "#1565c0",
                              backgroundColor: "#e3f2fd",
                            },
                          }}
                        >
                          View Document
                        </Button>
                      </Box>
                    </Box>
                  ) : null}

                  {/* cl.8.8.3 Assessment Questions - Always Visible */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 3,
                      backgroundColor: "#f8f9ff",
                      borderRadius: 2,
                      border: "1px solid #e3f2fd",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 2, color: "#1976d2" }}
                    >
                      ISO 15189: 2022 Clause 8.8.3
                    </Typography>
                    <Grid container spacing={3}>
                      {/* Trained Auditor */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Trained Auditor: as per ISO 15189: 2022
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.trainedAuditor}
                            onChange={handleInputChange("trainedAuditor")}
                          >
                            <FormControlLabel
                              value="Yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="No"
                              control={<Radio size="small" />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Independence of Activity */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Independence of Activity being audited:
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.independenceOfActivity}
                            onChange={handleInputChange(
                              "independenceOfActivity",
                            )}
                          >
                            <FormControlLabel
                              value="Yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="No"
                              control={<Radio size="small" />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* NCs Closed */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Status of the Non-Conformity raised during the
                          internal audit is closed or not? :
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.ncsClosed}
                            onChange={handleInputChange("ncsClosed")}
                          >
                            <FormControlLabel
                              value="Yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="No"
                              control={<Radio size="small" />}
                              label="No"
                            />
                            <FormControlLabel
                              value="NA"
                              control={<Radio size="small" />}
                              label="NA"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Effective */}
                      {/* <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Effective:
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={fieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.effective}
                            onChange={handleInputChange("effective")}
                          >
                            <FormControlLabel
                              value="Yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="No"
                              control={<Radio size="small" />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid> */}

                      {/* Frequency of Internal Audit */}
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Frequency of Internal Audit:
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          size="small"
                          value={formInputs.frequencyOfAudit}
                          onChange={handleInputChange("frequencyOfAudit")}
                          inputProps={{
                            readOnly: naf6FieldsDisabled,
                          }}
                          placeholder={getFieldPlaceholder(
                            "Enter frequency details...",
                          )}
                        />
                      </Grid>
                    </Grid>

                    {/* Display NCs for Clause 8.8.3 */}
                    {getNCsByClause("8.8.3").length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 2, color: "#1976d2" }}
                        >
                          Non-Conformities (Clause 8.8.3)
                        </Typography>
                        {getNCsByClause("8.8.3").map((nc, index) => (
                          <Box
                            key={nc.id}
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Description:</strong> {nc.description}
                            </Typography>
                            {nc.document &&
                              nc.document.split("~").filter(Boolean).length >
                                0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: colors.grey[700],
                                      mb: 1,
                                      display: "block",
                                    }}
                                  >
                                    Documents:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    {nc.document
                                      .split("~")
                                      .filter(Boolean)
                                      .map((doc, i) => {
                                        const lower = doc.toLowerCase();
                                        return (
                                          <WorkingSpaceDocument
                                            key={i}
                                            doc={doc}
                                            index={i}
                                            isImage={lower.match(
                                              /\.(jpg|jpeg|png|gif|webp)$/,
                                            )}
                                            isPdf={lower.endsWith(".pdf")}
                                            isExcel={lower.match(
                                              /\.(xls|xlsx)$/,
                                            )}
                                            colors={colors}
                                            onImageClick={handleOpenLightbox}
                                          />
                                        );
                                      })}
                                  </Box>
                                </Box>
                              )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                      >
                        Comments:
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        value={formInputs.auditComments}
                        onChange={handleInputChange("auditComments")}
                        inputProps={{
                          readOnly: naf6FieldsDisabled,
                        }}
                        placeholder={getFieldPlaceholder("Enter comments...")}
                      />
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* Management Review */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Management Review
                  </Typography>
                  {cabDetails?.ManagementReviewFromDate ||
                  cabDetails?.ManagementReviewToDate ? (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: "#1976d2", mb: 0.5 }}
                          >
                            📅 Management Review Dates:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#333" }}>
                            {formatQualityAssuranceDate(
                              cabDetails.ManagementReviewFromDate,
                              cabDetails.ManagementReviewToDate,
                            )}
                          </Typography>
                        </Box>
                        {cabDetails?.ManagementReviewDocument && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={handleViewManagementReviewDocument}
                            sx={{
                              borderColor: "#2e7d32",
                              color: "#2e7d32",
                              "&:hover": {
                                borderColor: "#1b5e20",
                                backgroundColor: "#e8f5e8",
                              },
                            }}
                          >
                            View Document
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : cabDetails?.ManagementReviewDocument ? (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#2e7d32" }}
                        >
                          📄 Management Review Document Available
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewManagementReviewDocument}
                          sx={{
                            borderColor: "#2e7d32",
                            color: "#2e7d32",
                            "&:hover": {
                              borderColor: "#1b5e20",
                              backgroundColor: "#e8f5e8",
                            },
                          }}
                        >
                          View Document
                        </Button>
                      </Box>
                    </Box>
                  ) : null}

                  {/* cl.8.9.1 Assessment Questions - Always Visible */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 3,
                      backgroundColor: "#f0f8f0",
                      borderRadius: 2,
                      border: "1px solid #e8f5e8",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 2, color: "#2e7d32" }}
                    >
                      ISO 15189: 2022 Clause 8.9
                    </Typography>
                    <Grid container spacing={3}>
                      {/* All points of Standard covered */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          All points of Standard covered:
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.allPointsCovered}
                            onChange={handleInputChange("allPointsCovered")}
                          >
                            <FormControlLabel
                              value="Yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="No"
                              control={<Radio size="small" />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Conducted within a year */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Conducted within a year:
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.conductedWithinYear}
                            onChange={handleInputChange("conductedWithinYear")}
                          >
                            <FormControlLabel
                              value="Yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="No"
                              control={<Radio size="small" />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Display NCs for Clause 8.9 */}
                    {getNCsByClause("8.9").length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 2, color: "#2e7d32" }}
                        >
                          Non-Conformities (Clause 8.9)
                        </Typography>
                        {getNCsByClause("8.9").map((nc, index) => (
                          <Box
                            key={nc.id}
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Description:</strong> {nc.description}
                            </Typography>
                            {nc.document &&
                              nc.document.split("~").filter(Boolean).length >
                                0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: colors.grey[700],
                                      mb: 1,
                                      display: "block",
                                    }}
                                  >
                                    Documents:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    {nc.document
                                      .split("~")
                                      .filter(Boolean)
                                      .map((doc, i) => {
                                        const lower = doc.toLowerCase();
                                        return (
                                          <WorkingSpaceDocument
                                            key={i}
                                            doc={doc}
                                            index={i}
                                            isImage={lower.match(
                                              /\.(jpg|jpeg|png|gif|webp)$/,
                                            )}
                                            isPdf={lower.endsWith(".pdf")}
                                            isExcel={lower.match(
                                              /\.(xls|xlsx)$/,
                                            )}
                                            colors={colors}
                                            onImageClick={handleOpenLightbox}
                                          />
                                        );
                                      })}
                                  </Box>
                                </Box>
                              )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                      >
                        Comments:
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        value={formInputs.managementComments}
                        onChange={handleInputChange("managementComments")}
                        inputProps={{
                          readOnly: naf6FieldsDisabled,
                        }}
                        placeholder={getFieldPlaceholder("Enter comments...")}
                      />
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* PT Participation */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    PT Participation
                  </Typography>

                  {/* Availability of PT */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        Availability of PT:
                      </Typography>
                      <FormControl
                        component="fieldset"
                        disabled={naf6FieldsDisabled}
                      >
                        <RadioGroup
                          row
                          value={formInputs.ptAvailability}
                          onChange={handleInputChange("ptAvailability")}
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio size="small" />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio size="small" />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Reason if PT is not available - Show only when No is selected */}
                  {formInputs.ptAvailability === "No" && (
                    <Box sx={{ mt: 2, mb: 3 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        Reason for PT not being available:
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        value={formInputs.ptNotAvailableReason}
                        onChange={handleInputChange("ptNotAvailableReason")}
                        inputProps={{
                          readOnly: fieldsDisabled,
                        }}
                        placeholder={getFieldPlaceholder(
                          "Enter reason why PT is not available...",
                        )}
                      />
                    </Box>
                  )}

                  {/* PT Participation Assessment - Always Visible */}
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "#fff8e1",
                      borderRadius: 2,
                      border: "1px solid #ffecb3",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#f57c00" }}
                      >
                        PT Participation Assessment
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewPTData}
                          sx={{
                            borderColor: "#f57c00",
                            color: "#f57c00",
                            "&:hover": {
                              borderColor: "#e65100",
                              backgroundColor: "#fff3e0",
                            },
                          }}
                        >
                          View PT Data
                        </Button>
                        {cabDetails?.QMSFile && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DescriptionIcon />}
                            onClick={handleViewQMSDocument}
                            sx={{
                              borderColor: "#1976d2",
                              color: "#1976d2",
                              "&:hover": {
                                borderColor: "#1565c0",
                                backgroundColor: "#e3f2fd",
                              },
                            }}
                          >
                            View PT Document
                          </Button>
                        )}
                      </Box>
                    </Box>
                    <Grid container spacing={3}>
                      {/* Coverage of scope */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Coverage of scope:
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.ptCoverageOfScope}
                            onChange={handleInputChange("ptCoverageOfScope")}
                          >
                            <FormControlLabel
                              value="Adequate"
                              control={<Radio size="small" />}
                              label="Adequate"
                            />
                            <FormControlLabel
                              value="Not Adequate"
                              control={<Radio size="small" />}
                              label="Not Adequate"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Outlier */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Outlier:
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.ptOutlier}
                            onChange={handleInputChange("ptOutlier")}
                          >
                            <FormControlLabel
                              value="Yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="No"
                              control={<Radio size="small" />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Root cause analysis remarks - Show only when Outlier is Yes */}
                      {formInputs.ptOutlier === "Yes" && (
                        <Grid item xs={12}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontWeight: 500 }}
                          >
                            Remarks on root cause analysis:
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            size="small"
                            value={formInputs.ptOutlierRemarks}
                            onChange={handleInputChange("ptOutlierRemarks")}
                            inputProps={{
                              readOnly: fieldsDisabled,
                            }}
                            placeholder={getFieldPlaceholder(
                              "Enter root cause analysis remarks...",
                            )}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* ILC Participation */}
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  {/* Assessment Questions */}
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "#f3e5f5",
                      borderRadius: 2,
                      border: "1px solid #e1bee7",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#7b1fa2" }}
                      >
                        ILC Assessment Questions
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={handleViewILCData}
                        sx={{
                          borderColor: "#7b1fa2",
                          color: "#7b1fa2",
                          "&:hover": {
                            borderColor: "#6a1b9a",
                            backgroundColor: "rgba(123, 31, 162, 0.08)",
                          },
                        }}
                      >
                        View ILC Data
                      </Button>
                    </Box>
                    <Grid container spacing={3}>
                      {/* Coverage of scope through PT and/or ILC */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Coverage of scope through PT and / or ILC:
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.ilcCoverageOfScope}
                            onChange={handleInputChange("ilcCoverageOfScope")}
                          >
                            <FormControlLabel
                              value="Adequate"
                              control={<Radio size="small" />}
                              label="Adequate"
                            />
                            <FormControlLabel
                              value="Not Adequate"
                              control={<Radio size="small" />}
                              label="Not Adequate"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Outlier */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Outlier:
                        </Typography>
                        <FormControl
                          component="fieldset"
                          disabled={naf6FieldsDisabled}
                        >
                          <RadioGroup
                            row
                            value={formInputs.ilcOutlier}
                            onChange={handleInputChange("ilcOutlier")}
                          >
                            <FormControlLabel
                              value="Yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="No"
                              control={<Radio size="small" />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Root cause analysis remarks - Show only when Outlier is Yes */}
                      {formInputs.ilcOutlier === "Yes" && (
                        <Grid item xs={12}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontWeight: 500 }}
                          >
                            Remarks on root cause analysis of the lab:
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            size="small"
                            value={formInputs.ilcOutlierRemarks}
                            onChange={handleInputChange("ilcOutlierRemarks")}
                            inputProps={{
                              readOnly: naf6FieldsDisabled,
                            }}
                            placeholder={getFieldPlaceholder(
                              "Enter root cause analysis remarks...",
                            )}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* Other Quality Assurance Measures */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Other Quality Assurance Measures
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    value={formInputs.otherQualityMeasures}
                    onChange={handleInputChange("otherQualityMeasures")}
                    inputProps={{
                      readOnly: naf6FieldsDisabled,
                    }}
                    placeholder={getFieldPlaceholder(
                      "Enter details about other quality assurance measures...",
                    )}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Summary of Assessment */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Summary of Assessment (NAF 6)
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                mb: 2,
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.6, whiteSpace: "pre-wrap" }}
              >
                {assessmentReportData?.assessmentSummary ||
                  "Loading Please wait..."}
              </Typography>
            </Box>

            {/* Assessment Team Recommendations */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Assessment Team Recommendations (NAF 6)
            </Typography>
            <Box sx={{ p: 2, bgcolor: "#e8f5e8", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {assessmentReportData?.recommendationOfAssessmentTeam ||
                  "Loading Please wait..."}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Observations/Comments by Concerned Team Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "purple", mr: 2, width: 40, height: 40 }}>
                <AssessmentIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Observations/ Comments by Concerned Team
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Advisor/Consultant/Internal Auditor */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Advisor / Consultant / Internal Auditor and their
                    Organization
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    value={formInputs.advisorConsultant}
                    onChange={handleInputChange("advisorConsultant")}
                    inputProps={{
                      readOnly: fieldsDisabled,
                    }}
                    placeholder={getFieldPlaceholder(
                      "Enter details about advisor/consultant/internal auditor and their organization...",
                    )}
                  />
                </Box>
              </Grid>

              {/* Adequacy of Organization */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Adequacy of the Organization and Procedures Adopted by the
                    CAB
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    value={formInputs.adequacyOrganization}
                    onChange={handleInputChange("adequacyOrganization")}
                    inputProps={{
                      readOnly: fieldsDisabled,
                    }}
                    placeholder={getFieldPlaceholder(
                      "Enter assessment of adequacy of organization and procedures to give confidence in competence...",
                    )}
                  />
                </Box>
              </Grid>

              {/* Other Information */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Any Other Information That Can Assist in Decision Making
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                        color: "text.secondary",
                        mt: 0.5,
                      }}
                    >
                      (Like misuse of NABL symbol, outcome of unannounced
                      audits, complaints)
                    </Typography>
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    value={formInputs.otherInformation}
                    onChange={handleInputChange("otherInformation")}
                    inputProps={{
                      readOnly: fieldsDisabled,
                    }}
                    placeholder={getFieldPlaceholder(
                      "Enter any other relevant information like misuse of NABL symbol, outcome of unannounced audits, complaints...",
                    )}
                  />
                </Box>
              </Grid>

              {/* Observations/Comments & Recommendations */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Additional Observation / Comment & Recommendation
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    size="small"
                    value={formInputs.observationRecommendation}
                    onChange={handleInputChange("observationRecommendation")}
                    inputProps={{
                      readOnly: fieldsDisabled,
                    }}
                    placeholder={getFieldPlaceholder(
                      "Enter observations, comments, and recommendations from the concerned team...",
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Additional Documents */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{ bgcolor: "secondary.main", mr: 2, width: 40, height: 40 }}
              >
                <DescriptionIcon />
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Additional Documents
                <Chip
                  label={additionalDocuments.length}
                  size="small"
                  sx={{
                    ml: 2,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Typography>
            </Box>

            {loadingDocuments ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : additionalDocuments.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <DescriptionIcon
                  sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                />
                <Typography variant="h6" color="textSecondary">
                  No additional documents found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  No additional documents have been uploaded for this assessment
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ borderRadius: 2, overflow: "hidden" }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        "& .MuiTableCell-head": {
                          color: "white",
                          fontWeight: 700,
                          fontSize: "1rem",
                          borderBottom: "none",
                        },
                      }}
                    >
                      <TableCell>S No.</TableCell>
                      <TableCell>Document Name</TableCell>
                      <TableCell>Target Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Upload Date</TableCell>
                      <TableCell>Uploaded By</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {additionalDocuments.map((document, index) => (
                      <TableRow
                        key={document.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#fafbfc" : "white",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "rgba(102, 126, 234, 0.08)",
                            transform: "scale(1.01)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: "#2c3e50" }}
                          >
                            {document.documentName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTargetTypeDisplay(document.targetType)}
                            color={getTargetTypeColor(document.targetType)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {document.description || "No description"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {document.uploadDate
                              ? formatDate(document.uploadDate)
                              : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {document.uploadedBy || "Unknown"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDocument(document)}
                            sx={{
                              borderColor: "primary.main",
                              color: "primary.main",
                              "&:hover": {
                                borderColor: "primary.dark",
                                backgroundColor: "primary.50",
                              },
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Comments Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            display: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Comments For Putting in AC Process
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={8}
                size="small"
                value={formInputs.acProcessComments}
                onChange={handleInputChange("acProcessComments")}
                inputProps={{
                  readOnly: fieldsDisabled,
                }}
                placeholder={getFieldPlaceholder(
                  "Enter comments for putting in AC process...",
                )}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Decision Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Decision to be process
            </Typography>
            <RadioGroup
              value={decisionProcess}
              onChange={(event) => setDecisionProcess(event.target.value)}
              disabled={fieldsDisabled}
              sx={{ display: "flex", flexDirection: "row", gap: 3 }}
            >
              <FormControlLabel
                value="internal_approval"
                control={
                  <Radio
                    color="success"
                    disabled={fieldsDisabled}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Through Internal Approval
                    </Typography>
                  </Box>
                }
                sx={{
                  margin: 0,
                  padding: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor:
                    decisionProcess === "internal_approval"
                      ? "success.50"
                      : getFieldBackgroundColor("transparent"),
                  transition: "all 0.2s ease",
                  "&:hover": !fieldsDisabled
                    ? {
                        borderColor: "success.main",
                        backgroundColor: "success.25",
                      }
                    : {},
                  ...(decisionProcess === "internal_approval" && {
                    borderColor: "success.main",
                    backgroundColor: "success.50",
                  }),
                  ...(fieldsDisabled && {
                    cursor: "not-allowed",
                    opacity: 0.7,
                  }),
                }}
              />
              <FormControlLabel
                value="acm"
                control={
                  <Radio
                    color="primary"
                    disabled={fieldsDisabled}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Through ACM
                  </Typography>
                }
                sx={{
                  margin: 0,
                  padding: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor:
                    decisionProcess === "acm"
                      ? "primary.50"
                      : getFieldBackgroundColor("transparent"),
                  transition: "all 0.2s ease",
                  "&:hover": !fieldsDisabled
                    ? {
                        borderColor: "primary.main",
                        backgroundColor: "primary.25",
                      }
                    : {},
                  ...(decisionProcess === "acm" && {
                    borderColor: "primary.main",
                    backgroundColor: "primary.50",
                  }),
                  ...(fieldsDisabled && {
                    cursor: "not-allowed",
                    opacity: 0.7,
                  }),
                }}
              />
            </RadioGroup>
            {showAfterApprovalBox && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Enter Details After Approval"
                  value={afterApprovalRemark}
                  disabled={disabledApprovalBox === false}
                  onChange={(e) => setAfterApprovalRemark(e.target.value)}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Submit Section */}
        <Card
          sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Success/Error Messages */}
            {submitSuccess && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  "& .MuiAlert-message": {
                    fontWeight: 600,
                  },
                }}
              >
                ✅ Form11 Assessment Summary has been saved successfully!
              </Alert>
            )}

            {updateSuccess && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  "& .MuiAlert-message": {
                    fontWeight: 600,
                  },
                }}
              >
                ✅ Form11 Assessment Summary has been updated successfully!
              </Alert>
            )}

            {submitError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  "& .MuiAlert-message": {
                    fontWeight: 600,
                  },
                }}
                onClose={() => setSubmitError(null)}
              >
                ❌ Error saving Form11: {submitError}
              </Alert>
            )}

            {updateError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  "& .MuiAlert-message": {
                    fontWeight: 600,
                  },
                }}
                onClose={() => setUpdateError(null)}
              >
                ❌ Error updating Form11: {updateError}
              </Alert>
            )}

            {/* Button Section */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Show Save button when no existing data */}
              {!isFormSaved && !hasExistingData && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSubmitForm}
                  disabled={isSubmitting || !appId}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "#e0e0e0",
                      color: "#9e9e9e",
                      boxShadow: "none",
                      transform: "none",
                    },
                  }}
                >
                  {isSubmitting ? "Saving Form11..." : "Save Form 11"}
                </Button>
              )}

              {/* Show Edit/Update/Cancel buttons when existing data */}
              {hasExistingData && (
                <>
                  {/* JDirector Rejection Details - Show when rejection reason exists */}
                  {existingForm11Data?.JDirectorDenyReason && (
                    <Alert
                      severity="error"
                      icon={<WarningIcon />}
                      sx={{
                        mb: 3,
                        backgroundColor: "#fff3e0",
                        border: "2px solid #ff9800",
                        "& .MuiAlert-icon": {
                          color: "#f57c00",
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 1, color: "#e65100" }}
                        >
                          Form Rejected by Joint Director
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Reason:</strong>{" "}
                          {existingForm11Data.JDirectorDenyReason}
                        </Typography>
                        {existingForm11Data?.JDirectorAcceptDate && (
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            <strong>Date:</strong>{" "}
                            {new Date(
                              existingForm11Data.JDirectorAcceptDate,
                            ).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  )}
                  {(applicationStage === "6c" || applicationStage === "21") &&
                    !afterApprovalRemark && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexWrap: "wrap",
                          justifyContent: "center",
                        }}
                      >
                        {!isEditMode ? (
                          <>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={<EditIcon />}
                              onClick={handleEditForm}
                              sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                background:
                                  "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                                boxShadow: "0 4px 15px rgba(255, 152, 0, 0.4)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #f57c00 0%, #e65100 100%)",
                                  boxShadow:
                                    "0 6px 20px rgba(255, 152, 0, 0.6)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              Edit Form 11
                            </Button>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={
                                isForwarding ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <AssignmentIcon />
                                )
                              }
                              onClick={handleForwardToJD}
                              disabled={isForwarding}
                              sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                background:
                                  "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                                boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #45a049 0%, #388e3c 100%)",
                                  boxShadow:
                                    "0 6px 20px rgba(76, 175, 80, 0.6)",
                                  transform: "translateY(-2px)",
                                },
                                "&:disabled": {
                                  background: "#e0e0e0",
                                  color: "#9e9e9e",
                                  boxShadow: "none",
                                  transform: "none",
                                },
                              }}
                            >
                              {isForwarding
                                ? "Forwarding to JD..."
                                : "Forward to JD"}
                            </Button>
                          </>
                        ) : (
                          // Update and Cancel buttons - shown when in edit mode
                          <>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={
                                isUpdating ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <SaveIcon />
                                )
                              }
                              onClick={handleUpdateForm}
                              disabled={isUpdating || !appId}
                              sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                background:
                                  "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
                                boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)",
                                  boxShadow:
                                    "0 6px 20px rgba(76, 175, 80, 0.6)",
                                  transform: "translateY(-2px)",
                                },
                                "&:disabled": {
                                  background: "#e0e0e0",
                                  color: "#9e9e9e",
                                  boxShadow: "none",
                                  transform: "none",
                                },
                              }}
                            >
                              {isUpdating
                                ? "Updating Form11..."
                                : "Update Form 11"}
                            </Button>

                            <Button
                              variant="outlined"
                              size="large"
                              onClick={handleCancelEdit}
                              disabled={isUpdating}
                              sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                borderColor: "#f44336",
                                color: "#f44336",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  borderColor: "#d32f2f",
                                  backgroundColor: "#ffebee",
                                  transform: "translateY(-1px)",
                                },
                                "&:disabled": {
                                  borderColor: "#e0e0e0",
                                  color: "#9e9e9e",
                                },
                              }}
                            >
                              Cancel Edit
                            </Button>
                          </>
                        )}
                      </Box>
                    )}

                  {(applicationStage === "6b" ||
                    applicationStage === "21" ||
                    applicationStage === "6c" ||
                    applicationStage === "6d" ||
                    applicationStage === "6e" ||
                    applicationStage === "7" ||
                    applicationStage === "7a" ||
                    applicationStage === "7b" ||
                    applicationStage === "7c" ||
                    applicationStage === "7e" ||
                    applicationStage === "7f") &&
                    showAfterApprovalBox && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexWrap: "wrap",
                          justifyContent: "center",
                        }}
                      >
                        {!isEditModeAfterUpdate ? (
                          <>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={<EditIcon />}
                              onClick={handleEditFormAfterApprove}
                              sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                background:
                                  "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                                boxShadow: "0 4px 15px rgba(255, 152, 0, 0.4)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #f57c00 0%, #e65100 100%)",
                                  boxShadow:
                                    "0 6px 20px rgba(255, 152, 0, 0.6)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              Edit Form 11 after Approve
                            </Button>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={
                                isForwarding ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <AssignmentIcon />
                                )
                              }
                              onClick={handleForwardToJD}
                              disabled={isForwarding}
                              sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                background:
                                  "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                                boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #45a049 0%, #388e3c 100%)",
                                  boxShadow:
                                    "0 6px 20px rgba(76, 175, 80, 0.6)",
                                  transform: "translateY(-2px)",
                                },
                                "&:disabled": {
                                  background: "#e0e0e0",
                                  color: "#9e9e9e",
                                  boxShadow: "none",
                                  transform: "none",
                                },
                              }}
                            >
                              {isForwarding
                                ? "Forwarding to JD..."
                                : "Forward to JD"}
                            </Button>
                          </>
                        ) : (
                          // Update and Cancel buttons - shown when in edit mode
                          <>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={
                                isUpdating ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <SaveIcon />
                                )
                              }
                              onClick={handleUpdateFormAfterApproval}
                              disabled={isUpdating || !appId}
                              sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                background:
                                  "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
                                boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)",
                                  boxShadow:
                                    "0 6px 20px rgba(76, 175, 80, 0.6)",
                                  transform: "translateY(-2px)",
                                },
                                "&:disabled": {
                                  background: "#e0e0e0",
                                  color: "#9e9e9e",
                                  boxShadow: "none",
                                  transform: "none",
                                },
                              }}
                            >
                              {isUpdating
                                ? "Updating Form11..."
                                : "Update Form 11 after Approval"}
                            </Button>

                            <Button
                              variant="outlined"
                              size="large"
                              onClick={handleCancelEdit}
                              disabled={isUpdating}
                              sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                borderColor: "#f44336",
                                color: "#f44336",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  borderColor: "#d32f2f",
                                  backgroundColor: "#ffebee",
                                  transform: "translateY(-1px)",
                                },
                                "&:disabled": {
                                  borderColor: "#e0e0e0",
                                  color: "#9e9e9e",
                                },
                              }}
                            >
                              Cancel Edit
                            </Button>
                          </>
                        )}
                      </Box>
                    )}
                </>
              )}
            </Box>

            {/* Data Already Exists Message */}
            {/* {hasExistingData && (
             <Grid container justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
               <Grid item>
                   <Button
                    variant="contained"
                    size="large"
                    startIcon={isForwarding ? <CircularProgress size={20} color="inherit" /> : <AssignmentIcon />}
                    onClick={handleForwardToJD}
                    disabled={isForwarding}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                      boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #45a049 0%, #388e3c 100%)',
                        boxShadow: '0 6px 20px rgba(76, 175, 80, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                      '&:disabled': {
                        background: '#e0e0e0',
                        color: '#9e9e9e',
                        boxShadow: 'none',
                        transform: 'none',
                      }
                    }}
                  >
                    {isForwarding ? 'Forwarding to JD...' : 'Forward to JD'}
                  </Button>
                </Grid>
              </Grid>
            )} */}
          </CardContent>
        </Card>

        {/* Forward to JD Confirmation Dialog */}
        <Dialog
          open={showForwardConfirm}
          onClose={handleForwardCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentIcon color="primary" />
              Confirm Forward to JD
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to forward this Form11 to Joint Director?
              This action cannot be undone and will move the application to the
              next stage.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleForwardCancel} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleForwardConfirm}
              variant="contained"
              color="primary"
              startIcon={<AssignmentIcon />}
            >
              Yes, Forward to JD
            </Button>
          </DialogActions>
        </Dialog>

        {/* Forward Success Alert */}
        {forwardSuccess && (
          <Alert
            severity="success"
            sx={{
              position: "fixed",
              top: "80px",
              right: "20px",
              zIndex: 9999,
              minWidth: "300px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              "& .MuiAlert-message": {
                fontWeight: 600,
              },
            }}
          >
            ✅ Form11 forwarded to JD successfully! Redirecting...
          </Alert>
        )}

        {/* Forward Error Alert */}
        {forwardError && (
          <Alert
            severity="error"
            sx={{
              position: "fixed",
              top: "80px",
              right: "20px",
              zIndex: 9999,
              minWidth: "300px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              "& .MuiAlert-message": {
                fontWeight: 600,
              },
            }}
            onClose={() => setForwardError(null)}
          >
            ❌ Error forwarding to JD: {forwardError}
          </Alert>
        )}
      </Box>

      {/* PT Data Popup Dialog */}
      <Dialog
        open={ptDataPopupOpen}
        onClose={handleClosePTDataPopup}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#f57c00",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AssessmentIcon />
            <Typography variant="h6" component="span">
              PT Participation Data
            </Typography>
          </Box>
          <IconButton
            onClick={handleClosePTDataPopup}
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {ptDataLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 4,
              }}
            >
              <CircularProgress sx={{ color: "#f57c00" }} />
              <Typography sx={{ ml: 2, color: "text.secondary" }}>
                Loading PT data...
              </Typography>
            </Box>
          ) : ptDataError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {ptDataError}
              </Alert>
              <Button
                variant="outlined"
                onClick={loadPTData}
                startIcon={<VisibilityIcon />}
              >
                Retry
              </Button>
            </Box>
          ) : ptData && ptData.length > 0 ? (
            <TableContainer sx={{ maxHeight: "60vh" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        bgcolor: "#f57c00",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Participation In
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#f57c00",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Provider Name
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#f57c00",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      ILC Details
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#f57c00",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Remarks
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#f57c00",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Created On
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ptData.map((row, index) => (
                    <TableRow
                      key={row.Id || index}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "rgba(245, 124, 0, 0.05)",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(245, 124, 0, 0.1)",
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={row.participationIn || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: "#fff3e0",
                            color: "#f57c00",
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getProviderName(
                            row.participationIn,
                            row.nodalLaboratory,
                            row.nodalLaboratoryOthers,
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={row.ilcDetails || "N/A"}
                        >
                          {row.ilcDetails || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={row.remarks || "N/A"}
                        >
                          {row.remarks || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {row.CreatedOn
                            ? new Date(row.CreatedOn).toLocaleDateString(
                                "en-IN",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "2-digit",
                                },
                              )
                            : "N/A"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <InfoIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
                No PT Data Found
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No proficiency testing participation data has been saved for
                this application yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#fafafa" }}>
          <Button
            onClick={handleClosePTDataPopup}
            variant="outlined"
            sx={{
              borderColor: "#f57c00",
              color: "#f57c00",
              "&:hover": {
                borderColor: "#e65100",
                bgcolor: "#fff3e0",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ILC Data Popup Dialog */}
      <Dialog
        open={ilcDataPopupOpen}
        onClose={handleCloseILCDataPopup}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#7b1fa2",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AssessmentIcon />
            <Typography variant="h6" component="span">
              ILC Participation Data
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseILCDataPopup}
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {ilcDataLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 4,
              }}
            >
              <CircularProgress sx={{ color: "#7b1fa2" }} />
              <Typography sx={{ ml: 2, color: "text.secondary" }}>
                Loading ILC data...
              </Typography>
            </Box>
          ) : ilcDataError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {ilcDataError}
              </Alert>
              <Button
                variant="outlined"
                onClick={loadILCData}
                startIcon={<VisibilityIcon />}
              >
                Retry
              </Button>
            </Box>
          ) : ilcData && ilcData.length > 0 ? (
            <TableContainer sx={{ maxHeight: "60vh" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        bgcolor: "#7b1fa2",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Participation In
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#7b1fa2",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Provider Name
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#7b1fa2",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      ILC Details
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#7b1fa2",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Remarks
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#7b1fa2",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Created On
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ilcData.map((row, index) => (
                    <TableRow
                      key={row.Id || index}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "rgba(123, 31, 162, 0.05)",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(123, 31, 162, 0.1)",
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={row.participationIn || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: "#f3e5f5",
                            color: "#7b1fa2",
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getProviderName(
                            row.participationIn,
                            row.nodalLaboratory,
                            row.nodalLaboratoryOthers,
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={row.ilcDetails || "N/A"}
                        >
                          {row.ilcDetails || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={row.remarks || "N/A"}
                        >
                          {row.remarks || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {row.CreatedOn
                            ? new Date(row.CreatedOn).toLocaleDateString(
                                "en-IN",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "2-digit",
                                },
                              )
                            : "N/A"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <InfoIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
                No ILC Data Found
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No ILC participation data has been saved for this application
                yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#fafafa" }}>
          <Button
            onClick={handleCloseILCDataPopup}
            variant="outlined"
            sx={{
              borderColor: "#7b1fa2",
              color: "#7b1fa2",
              "&:hover": {
                borderColor: "#6a1b9a",
                bgcolor: "#f3e5f5",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox for NC Document Images */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.7)",
              },
              zIndex: 1,
            }}
          >
            ✕
          </IconButton>
          <img
            src={lightboxImage}
            alt="Document Preview"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Form11;
